import type { Database } from 'sql.js';
import { queryAll, queryOne, type SqlJsBind } from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import { filterEntregasCatalogMax } from '../../../../shared/entrega/domain/entregaCatalogLimits.ts';
import type { CatalogRow, PuntosEntregaCatalogoRow } from '../../../domain/puntoEntregaModel.ts';

type SqlBind = SqlJsBind;

function mapCatalogRow(r: Record<string, SqlBind>): CatalogRow {
  return { id: Number(r.id), nombre: String(r.nombre ?? '') };
}

function tableExists(db: Database, name: string): boolean {
  return queryOne(db, `SELECT 1 FROM sqlite_master WHERE type='table' AND name=?`, [name]) != null;
}

export function listEntregasCatalog(db: Database): CatalogRow[] {
  const rows = queryAll(db, 'SELECT id, nombre FROM entregas WHERE estado = 1 ORDER BY id', []).map(mapCatalogRow);
  return filterEntregasCatalogMax(rows);
}

export function listComunasCatalog(db: Database): CatalogRow[] {
  return queryAll(
    db,
    `SELECT id, nombre FROM comunas
     WHERE estado = 1
     ORDER BY CAST(public_id AS INTEGER), id`,
    [],
  ).map(mapCatalogRow);
}

export function listBarriosByComunaCatalog(db: Database, comunaId: number): CatalogRow[] {
  return queryAll(db, 'SELECT id, nombre FROM barrios WHERE comuna_id = ? AND estado = 1 ORDER BY id', [
    comunaId,
  ]).map(mapCatalogRow);
}

export function listLugaresByBarrioCatalog(db: Database, barrioId: number): CatalogRow[] {
  return queryAll(db, 'SELECT id, nombre FROM lugares WHERE barrio_id = ? AND estado = 1 ORDER BY id', [
    barrioId,
  ]).map(mapCatalogRow);
}

export function listPuntosEntregaCatalogoByComunaCatalog(db: Database, comunaId: number): CatalogRow[] {
  if (!tableExists(db, 'puntos_entrega_catalogo') || comunaId <= 0) return [];
  return queryAll(
    db,
    `SELECT id, nombre FROM puntos_entrega_catalogo
     WHERE comuna_id = ? AND estado = 1
     ORDER BY nombre COLLATE NOCASE, id`,
    [comunaId],
  ).map(mapCatalogRow);
}

export function findPuntosEntregaCatalogoById(db: Database, id: number): PuntosEntregaCatalogoRow | null {
  if (!tableExists(db, 'puntos_entrega_catalogo') || id <= 0) return null;
  const row = queryOne(
    db,
    `SELECT id, nombre, direccion, comuna_id FROM puntos_entrega_catalogo WHERE id = ? AND estado = 1`,
    [id],
  );
  if (!row) return null;
  return {
    id: Number(row.id),
    nombre: String(row.nombre ?? ''),
    direccion: String(row.direccion ?? ''),
    comunaId: Number(row.comuna_id ?? row.COMUNA_ID ?? 0),
  };
}

export function listLugaresByComunaCatalog(db: Database, comunaId: number): CatalogRow[] {
  const synced = listPuntosEntregaCatalogoByComunaCatalog(db, comunaId);
  if (synced.length > 0) return synced;
  if (comunaId <= 0) return [];
  return queryAll(
    db,
    `SELECT l.id, l.nombre FROM lugares l
     INNER JOIN barrios b ON b.id = l.barrio_id
     WHERE b.comuna_id = ? AND l.estado = 1 AND b.estado = 1
     ORDER BY l.nombre COLLATE NOCASE, l.id`,
    [comunaId],
  ).map(mapCatalogRow);
}

export function listTiposDocumentoCatalog(db: Database): CatalogRow[] {
  return queryAll(db, 'SELECT id, nombre FROM tipos_documentos WHERE estado = 1 ORDER BY id', []).map(mapCatalogRow);
}

export function listTiposBonosCatalog(db: Database): CatalogRow[] {
  if (!tableExists(db, 'tipos_bonos')) return [];
  return queryAll(db, 'SELECT id, nombre FROM tipos_bonos WHERE estado = 1 ORDER BY id', []).map(mapCatalogRow);
}

export function listParentescoCatalog(db: Database): CatalogRow[] {
  if (!tableExists(db, 'parentesco')) return [];
  return queryAll(db, 'SELECT id, nombre FROM parentesco WHERE estado = 1 ORDER BY id', []).map(mapCatalogRow);
}
